import React from 'react';
import { Input, InputNumber, Switch, TimePicker, Button } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

// Editors for the content blocks. Each is controlled: it gets the block's
// `data` and reports a whole new `data` object on every change; the caller
// decides when to save. Limits mirror src/blocks.js.

const dayName = (index, lang) =>
  new Date(Date.UTC(2024, 0, 1 + index)).toLocaleDateString(lang, { weekday: 'long', timeZone: 'UTC' });

export const HoursEditor = ({ value, onChange }) => {
  const [t, i18n] = useTranslation();
  const lang = i18n.language.slice(0, 2) === 'en' ? 'en' : 'tr';
  const setDay = (index, changes) => onChange({ ...value, days: value.days.map((day, i) => (i === index ? { ...day, ...changes } : day)) });

  // Monday's hours onto Tuesday-Friday, the usual case
  const copyWeekdays = () => onChange({ ...value, days: value.days.map((day, i) => (i > 0 && i < 5 ? { ...value.days[0] } : day)) });

  return (
    <div className="dash-hours">
      {value.days.map((day, i) => (
        <div key={i} className="dash-hours__row">
          <span className="dash-hours__day">{dayName(i, lang)}</span>
          <Switch size="small" checked={!day.closed} aria-label={dayName(i, lang)} onChange={open => setDay(i, open ? { closed: false, open: day.open || '09:00', close: day.close || '18:00' } : { closed: true })} />
          {day.closed
            ? <span className="dash-muted">{t('blocks.closed')}</span>
            : <TimePicker.RangePicker
                format="HH:mm" minuteStep={15} order={false} allowClear={false} inputReadOnly
                value={[dayjs(day.open, 'HH:mm'), dayjs(day.close, 'HH:mm')]}
                onChange={range => range && setDay(i, { open: range[0].format('HH:mm'), close: range[1].format('HH:mm') })} />}
        </div>
      ))}
      <div className="dash-hours__tools">
        <Button icon={<CopyOutlined />} onClick={copyWeekdays}>{t('blocks.copyWeekdays')}</Button>
        <span className="dash-hint">{t('blocks.overnightHint')}</span>
      </div>
      <label className="dash-field">
        <span>{t('blocks.note')}</span>
        <Input value={value.note} maxLength={120} placeholder={t('blocks.notePlaceholder')} onChange={e => onChange({ ...value, note: e.target.value })} />
      </label>
    </div>
  );
};

export const LocationEditor = ({ value, onChange }) => {
  const [t] = useTranslation();
  return (
    <div className="dash-grid">
      <label className="dash-field dash-field--wide">
        <span>{t('blocks.address')}</span>
        <Input.TextArea value={value.address} maxLength={300} autoSize={{ minRows: 2, maxRows: 4 }} placeholder={t('blocks.addressPlaceholder')}
          onChange={e => onChange({ ...value, address: e.target.value })} />
      </label>
      <label className="dash-inline">
        <Switch size="small" checked={!!value.showMap} onChange={showMap => onChange({ ...value, showMap })} /> {t('blocks.showMap')}
      </label>
    </div>
  );
};

export const MenuEditor = ({ value, onChange }) => {
  const [t] = useTranslation();
  const items = value.items || [];
  const setItems = next => onChange({ ...value, items: next });
  const setItem = (index, changes) => setItems(items.map((item, i) => (i === index ? { ...item, ...changes } : item)));
  const move = (index, by) => {
    const next = [...items];
    const [item] = next.splice(index, 1);
    next.splice(index + by, 0, item);
    setItems(next);
  };

  return (
    <div className="dash-menu">
      {items.map((item, i) => (
        <div key={i} className="dash-menu__item">
          <div className="dash-menu__fields">
            <Input value={item.name} maxLength={80} placeholder={t('blocks.itemName')} onChange={e => setItem(i, { name: e.target.value })} />
            <InputNumber value={item.price} min={0} max={10000000} step={5} placeholder={t('blocks.price')} addonAfter="₺" decimalSeparator=","
              onChange={price => setItem(i, { price: price === null ? null : price })} />
            <Input className="dash-menu__desc" value={item.description} maxLength={200} placeholder={t('blocks.itemDescription')} onChange={e => setItem(i, { description: e.target.value })} />
          </div>
          <div className="dash-menu__actions">
            <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} aria-label={t('blocks.moveUp')} />
            <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label={t('blocks.moveDown')} />
            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label={t('dash.delete')} />
          </div>
        </div>
      ))}
      <Button icon={<PlusOutlined />} onClick={() => setItems([...items, { name: '', description: '', price: null }])} disabled={items.length >= 60}>
        {t('blocks.addItem')}
      </Button>
    </div>
  );
};

export const BLOCK_EDITORS = { hours: HoursEditor, location: LocationEditor, menu: MenuEditor };

// Whether the server would accept this data (see src/blocks.js), so the
// Save button can say so before a round trip
export const isBlockValid = (kind, data) => {
  if (kind === 'hours') return data.days.every(day => day.closed || (day.open && day.close && day.open !== day.close));
  if (kind === 'location') return !!(data.address || '').trim();
  if (kind === 'menu') return (data.items || []).some(item => (item.name || '').trim());
  return false;
};
